import * as Blockly from 'blockly';
import type { HiddenFieldDef } from '../core/blockly-types.ts';
import { createChevronField, registerMutatorIfMissing } from './blockly-field-factory.ts';

/**
 * Mixin for the hidden_fields_toggle mutator extension.
 * Hidden fields are created once during init and toggled with setVisible().
 * This allows withNested / shadow blocks to work exactly like normal fields.
 * The only difference: hidden fields start invisible and appear on chevron click.
 */
const HiddenFieldsMixin: any = {
    _expanded: false,
    _hiddenFieldDefs: null as HiddenFieldDef[] | null,
    _hiddenInputsInitialized: false,

    /** Ensure per-instance objects are initialized (avoids shared mixin references) */
    _ensureOwnProperties: function (): void {
        if (!this._hiddenFieldDefs) this._hiddenFieldDefs = [];
    },

    /**
     * Create all hidden inputs once. Called from updateShape_ on first run.
     * Inputs persist for the block's lifetime; visibility is toggled with setVisible.
     */
    _initHiddenInputs: function (): void {
        if (this._hiddenInputsInitialized) return;
        this._hiddenInputsInitialized = true;
        this._ensureOwnProperties();

        for (const def of this._hiddenFieldDefs) {
            if (def.argDef.type === 'input_value') {
                const input = this.appendValueInput(def.name);
                if (def.argDef.check) {
                    input.setCheck(def.argDef.check);
                }
                if (def.label) {
                    input.appendField(def.label);
                }
            } else if (def.argDef.type === 'input_statement') {
                const input = this.appendStatementInput(def.name);
                if (def.argDef.check) {
                    input.setCheck(def.argDef.check);
                }
                if (def.label) {
                    input.appendField(def.label);
                }
            } else {
                // Field type (number, text, checkbox, dropdown, etc.) on own dummy input
                const input = this.appendDummyInput('HIDDEN_' + def.name);
                if (def.label) {
                    input.appendField(def.label);
                }
                const fieldInstance = this._createFieldFromDef(def);
                if (fieldInstance) {
                    input.appendField(fieldInstance, def.name);
                }
            }
        }

        // Add chevron toggle row at the end
        const chevronInput = this.appendDummyInput('HIDDEN_CHEVRON_ROW');
        const chevronField = createChevronField(this._expanded);
        chevronInput.appendField(chevronField, 'HIDDEN_TOGGLE');

        // Apply initial visibility
        this._applyVisibility();
    },

    /**
     * Set visibility on all hidden inputs based on _expanded state
     */
    _applyVisibility: function (): void {
        for (const def of this._hiddenFieldDefs) {
            const inputName = (def.argDef.type === 'input_value' || def.argDef.type === 'input_statement')
                ? def.name
                : 'HIDDEN_' + def.name;
            const input = this.getInput(inputName);
            if (input) {
                input.setVisible(this._expanded);
            }
        }
    },

    /**
     * Serialize state: only expanded flag needed.
     * Field values and connected blocks are serialized by Blockly's normal mechanism
     * since hidden inputs always exist on the block.
     */
    saveExtraState: function (): any {
        return { expanded: this._expanded };
    },

    /**
     * Deserialize state: restore expanded flag and apply visibility.
     * Also handles migration from old format (hiddenValues + connectedBlocks).
     */
    loadExtraState: function (state: any): void {
        this._ensureOwnProperties();
        this._expanded = state?.expanded ?? false;

        // Ensure inputs exist before applying state
        if (!this._hiddenInputsInitialized) {
            this._initHiddenInputs();
        }

        // Migration from old format: restore field values
        if (state?.hiddenValues) {
            for (const [name, value] of Object.entries(state.hiddenValues)) {
                const field = this.getField(name);
                if (field && value !== undefined && value !== null) {
                    field.setValue(value);
                }
            }
        }

        // Migration from old format: restore connected blocks
        if (state?.connectedBlocks) {
            for (const [name, blockData] of Object.entries(state.connectedBlocks)) {
                if (blockData && this.workspace) {
                    const input = this.getInput(name);
                    if (input && input.connection && !input.connection.targetBlock()) {
                        try {
                            const restoredBlock = Blockly.serialization.blocks.append(blockData as any, this.workspace);
                            if (restoredBlock) {
                                const conn = restoredBlock.outputConnection || restoredBlock.previousConnection;
                                if (conn) conn.connect(input.connection);
                            }
                        } catch (e) {
                            console.warn('Migration: Failed to restore hidden connected block:', name, e);
                        }
                    }
                }
            }
        }

        this._applyVisibility();

        // Update chevron visual
        const chevronField = this.getField('HIDDEN_TOGGLE');
        if (chevronField) {
            (chevronField as any).setExpanded(this._expanded);
        }
    },

    /**
     * XML serialization: serialize state to a <mutation> DOM element.
     */
    mutationToDom: function (): Element {
        const mutation = Blockly.utils.xml.createElement('mutation');
        const state = this.saveExtraState();
        if (state) {
            mutation.setAttribute('hidden_state', JSON.stringify(state));
        }
        return mutation;
    },

    /**
     * XML deserialization: restore state from a <mutation> DOM element.
     */
    domToMutation: function (xmlElement: Element): void {
        const stateStr = xmlElement.getAttribute('hidden_state');
        if (stateStr) {
            try {
                const state = JSON.parse(stateStr);
                this.loadExtraState(state);
            } catch (e) {
                console.warn('Failed to parse hidden fields mutation state:', e);
            }
        }
    },

    /**
     * Toggle visibility of hidden fields (called by FieldChevron click)
     */
    toggleHiddenFields: function (): void {
        this._expanded = !this._expanded;
        this._applyVisibility();

        // Update chevron visual
        const chevronField = this.getField('HIDDEN_TOGGLE');
        if (chevronField) {
            (chevronField as any).setExpanded(this._expanded);
        }

        if (this.rendered) {
            this.render();
            this.bumpNeighbours();
        }
    },

    /**
     * Get the value of a hidden field (backwards compatibility for code generation).
     * With the new approach, fields always exist on the block, so getField/getInputTargetBlock
     * should find them directly. This is a fallback.
     */
    getHiddenFieldValue: function (name: string): any {
        this._ensureOwnProperties();
        const field = this.getField(name);
        if (field) {
            return field.getValue();
        }
        const def = this._hiddenFieldDefs.find((d: HiddenFieldDef) => d.name === name);
        if (def && (def.argDef.type === 'input_value' || def.argDef.type === 'input_statement')) {
            const targetBlock = this.getInputTargetBlock(name);
            if (targetBlock) {
                return undefined; // Let normal code generation handle it
            }
            return def.defaultValue ?? '';
        }
        return def?.defaultValue ?? '';
    },

    /**
     * Called from init wrapper and loadExtraState.
     * On first call: creates all hidden inputs.
     * On subsequent calls: just applies visibility.
     */
    updateShape_: function (): void {
        this._ensureOwnProperties();
        if (!this._hiddenInputsInitialized) {
            this._initHiddenInputs();
        } else {
            this._applyVisibility();
            // Update chevron visual
            const chevronField = this.getField('HIDDEN_TOGGLE');
            if (chevronField) {
                (chevronField as any).setExpanded(this._expanded);
            }
        }
    },

    /**
     * Create a Blockly Field instance from a hidden field definition
     */
    _createFieldFromDef: function (def: HiddenFieldDef): Blockly.Field | null {
        const argDef = def.argDef as any;

        switch (argDef.type) {
            case 'field_input': {
                return new Blockly.FieldTextInput(argDef.text ?? '');
            }
            case 'field_number': {
                return new Blockly.FieldNumber(argDef.value ?? 0);
            }
            case 'field_checkbox': {
                const checked = argDef.checked ?? false;
                return new Blockly.FieldCheckbox(checked ? 'TRUE' : 'FALSE');
            }
            case 'field_dropdown': {
                return new Blockly.FieldDropdown(argDef.options ?? [['?', '?']]);
            }
            case 'field_multiline_modal': {
                const FieldClass = (Blockly.fieldRegistry as any).getClass?.('field_multiline_modal')
                    ?? (Blockly as any).registry?.getClass?.('field', 'field_multiline_modal');
                if (FieldClass) {
                    return new (FieldClass as any)(argDef.text ?? '');
                }
                return new Blockly.FieldTextInput(argDef.text ?? '');
            }
            case 'field_json_modal': {
                const FieldClass = (Blockly.fieldRegistry as any).getClass?.('field_json_modal')
                    ?? (Blockly as any).registry?.getClass?.('field', 'field_json_modal');
                if (FieldClass) {
                    return new (FieldClass as any)(argDef.text ?? '{}');
                }
                return new Blockly.FieldTextInput(argDef.text ?? '{}');
            }
            default:
                return new Blockly.FieldTextInput('');
        }
    }
};

/**
 * Register the hidden_fields_toggle extension.
 * Must be called once before any blocks using it are created.
 */
let _registered = false;
export function registerHiddenFieldsExtension(): void {
    if (_registered) return;
    _registered = true;

    registerMutatorIfMissing(
        'hidden_fields_toggle',
        HiddenFieldsMixin,
        undefined,
        []
    );
}
